import { useMemo } from "react";
import { formatDistanceToNow } from "date-fns";
import {
  AlarmClock,
  CheckCircle2,
  Clock,
  CornerDownRight,
  MessageSquareText,
  ShieldAlert,
  TimerReset,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import type {
  DiscountApprovalEvent,
  DiscountApprovalRequest,
  DiscountApprovalStep,
} from "@/lib/api/discountApprovals";

type DiscountApprovalTimelineProps = {
  request: DiscountApprovalRequest;
  steps: DiscountApprovalStep[];
  events: DiscountApprovalEvent[];
};

const statusVariant = (status: DiscountApprovalStep["status"]) => {
  switch (status) {
    case "approved":
      return "bg-emerald-500/10 text-emerald-600 border-emerald-500/30 dark:text-emerald-300";
    case "declined":
      return "bg-rose-500/10 text-rose-600 border-rose-500/30 dark:text-rose-300";
    case "waiting":
      return "bg-amber-500/10 text-amber-600 border-amber-500/30 dark:text-amber-300";
    case "skipped":
      return "bg-slate-500/10 text-slate-600 border-slate-500/30 dark:text-slate-300";
    default:
      return "bg-primary/10 text-primary border-primary/20";
  }
};

const statusIcon = (status: DiscountApprovalStep["status"]) => {
  switch (status) {
    case "approved":
      return CheckCircle2;
    case "declined":
      return ShieldAlert;
    case "waiting":
      return TimerReset;
    case "skipped":
      return CornerDownRight;
    default:
      return Clock;
  }
};

const isPending = (status: DiscountApprovalStep["status"]) =>
  status === "pending" || status === "waiting";

export const DiscountApprovalTimeline = ({
  request,
  steps,
  events,
}: DiscountApprovalTimelineProps) => {
  const orderedSteps = useMemo(
    () => [...steps].sort((a, b) => a.stepOrder - b.stepOrder),
    [steps],
  );

  const sortedEvents = useMemo(
    () =>
      [...events].sort(
        (a, b) =>
          new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime(),
      ),
    [events],
  );

  const currentStepId = useMemo(() => {
    const step = orderedSteps.find((entry) => isPending(entry.status));
    return step?.id ?? null;
  }, [orderedSteps]);

  return (
    <div className="rounded-xl border border-border/60 bg-card/80 p-5">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-foreground">Approval timeline</p>
          <p className="text-xs text-muted-foreground">
            Track who has approved, declined, or escalated this request.
          </p>
        </div>
        <div className="flex items-center gap-2 rounded-md bg-muted/40 px-3 py-1.5 text-xs text-muted-foreground">
          <AlarmClock className="h-3.5 w-3.5" />
          <span>
            {orderedSteps.length} step{orderedSteps.length === 1 ? "" : "s"}
          </span>
        </div>
      </div>

      <div className="relative">
        <div className="absolute left-4 top-2 bottom-2 w-px bg-border" aria-hidden />
        <div className="space-y-4">
          {orderedSteps.map((step) => {
            const Icon = statusIcon(step.status);
            const distanceLabel = step.respondedAt
              ? formatDistanceToNow(new Date(step.respondedAt), { addSuffix: true })
              : request.status === "approved" || request.status === "declined"
                ? "Awaiting record"
                : "Pending";

            return (
              <div key={step.id} className="relative pl-10">
                <span
                  className={cn(
                    "absolute left-2 top-1 flex h-5 w-5 items-center justify-center rounded-full border text-[10px]",
                    statusVariant(step.status),
                    currentStepId === step.id && isPending(step.status)
                      ? "ring-2 ring-primary/40"
                      : "",
                  )}
                >
                  <Icon className="h-3 w-3" />
                </span>
                <div className="rounded-lg border border-border/60 bg-background/80 p-3">
                  <div className="flex flex-wrap items-center gap-2 text-sm font-medium text-foreground">
                    <span>
                      Step {step.stepOrder + 1}: {step.approverRole}
                    </span>
                    {step.status ? (
                      <Badge
                        className={cn(
                          "border text-[11px] capitalize",
                          statusVariant(step.status),
                        )}
                      >
                        {step.status.replaceAll("_", " ")}
                      </Badge>
                    ) : null}
                    {currentStepId === step.id && isPending(step.status) ? (
                      <Badge variant="secondary" className="text-[10px] uppercase">
                        Current
                      </Badge>
                    ) : null}
                  </div>
                  <div className="mt-2 grid gap-1 text-xs text-muted-foreground md:grid-cols-2">
                    <div>
                      <span className="font-medium text-foreground">Approver</span>:{" "}
                      {step.approverId ?? "Unassigned"}
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      <span>{distanceLabel}</span>
                    </div>
                    {step.responseNote ? (
                      <div className="col-span-2 rounded-md bg-muted/40 px-2 py-1.5 text-[12px] text-foreground">
                        “{step.responseNote}”
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <Separator className="my-5" />

      <div className="space-y-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <MessageSquareText className="h-4 w-4 text-primary" />
          Activity feed
        </div>
        {sortedEvents.length === 0 ? (
          <p className="rounded-lg border border-dashed border-muted-foreground/40 bg-muted/20 px-3 py-4 text-xs text-muted-foreground">
            No activity recorded yet. Actions will appear here as the approval progresses.
          </p>
        ) : (
          <div className="space-y-2">
            {sortedEvents.slice(0, 8).map((event) => (
              <div
                key={event.id}
                className="rounded-lg border border-border/60 bg-background/70 px-3 py-2 text-xs"
              >
                <div className="flex flex-wrap items-center gap-2 text-foreground">
                  <Badge variant="secondary" className="text-[10px] uppercase">
                    {event.eventType.replaceAll("_", " ")}
                  </Badge>
                  <span className="text-muted-foreground">
                    {formatDistanceToNow(new Date(event.occurredAt), { addSuffix: true })}
                  </span>
                </div>
                <div className="mt-1 text-muted-foreground">
                  {event.actorRole ? (
                    <span className="mr-1 text-[11px] uppercase tracking-wide text-foreground/80">
                      {event.actorRole}
                    </span>
                  ) : null}
                  {event.actorId ?? "System"}
                </div>
                {Object.keys(event.payload ?? {}).length > 0 ? (
                  <pre className="mt-2 max-h-28 overflow-auto rounded-md bg-muted/40 px-2 py-1 text-[10px] text-muted-foreground">
                    {JSON.stringify(event.payload, null, 2)}
                  </pre>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
